import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  NgZone,
  OnDestroy,
  Output,
  PLATFORM_ID,
  ViewChild,
  signal,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { AbstractControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ccAside, ccCard, ccFields } from './consult-form.animations';

export interface CcTrust {
  id: string;
  title: string;
  desc: string;
  tone: 'pink' | 'violet';
  icon: 'bolt' | 'shield' | 'users';
}

/**
 * The consultation card: intro + trust points on the left, the enquiry form on
 * the right. Presentation only — the host page owns the FormGroup, the submit
 * call and the API wiring, so this can drop into any banner variant.
 */
@Component({
  selector: 'app-consult-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './consult-form.component.html',
  styleUrls: ['./consult-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [ccCard, ccAside, ccFields],
})
export class ConsultFormComponent implements AfterViewInit, OnDestroy {
  /** The page's enquiry FormGroup — controls: firstname, lastname, Number, email, msg. */
  @Input({ required: true }) form!: FormGroup;

  /** Mirrors the page's in-flight flag so the button can show its spinner. */
  @Input() sending = false;

  /** Single-column layout for the narrow (non-hero) banner slot. */
  @Input() compact = false;

  @Output() send = new EventEmitter<void>();

  @ViewChild('ccRoot') ccRoot?: ElementRef<HTMLElement>;

  readonly viewState = signal<'hidden' | 'shown'>('hidden');

  readonly trust: CcTrust[] = [
    {
      id: 'quick',
      title: 'Quick Response',
      desc: 'We reply within 1 business day',
      tone: 'pink',
      icon: 'bolt',
    },
    {
      id: 'safe',
      title: '100% Confidential',
      desc: 'Your information is always safe with us',
      tone: 'violet',
      icon: 'shield',
    },
    {
      id: 'expert',
      title: 'Expert Support',
      desc: 'Get guidance from our industry experts',
      tone: 'pink',
      icon: 'users',
    },
  ];

  private io?: IntersectionObserver;

  constructor(
    private zone: NgZone,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  trackById(_i: number, item: CcTrust): string {
    return item.id;
  }

  /* ------------------------------------------------------------ controls -- */

  get firstname(): AbstractControl | null {
    return this.form?.get('firstname') ?? null;
  }
  get lastname(): AbstractControl | null {
    return this.form?.get('lastname') ?? null;
  }
  get contact(): AbstractControl | null {
    return this.form?.get('Number') ?? null;
  }
  get email(): AbstractControl | null {
    return this.form?.get('email') ?? null;
  }
  get msg(): AbstractControl | null {
    return this.form?.get('msg') ?? null;
  }

  /** True once the control should show its error block. */
  bad(c: AbstractControl | null): boolean {
    return !!c && c.invalid && c.touched;
  }

  /** True while the label should sit in its raised state. */
  filled(c: AbstractControl | null): boolean {
    const v = c?.value;
    return typeof v === 'string' ? v.trim().length > 0 : v != null && v !== '';
  }

  /* --------------------------------------------------------- key guards -- */

  /** Blocks a leading space, matching the behaviour the pages relied on. */
  noSpace(event: KeyboardEvent): boolean {
    const el = event.target as HTMLInputElement | HTMLTextAreaElement | null;
    if (event.keyCode === 32 && !el?.value) return false;
    return true;
  }

  /** Digits only, for the mobile field. */
  numberOnly(event: KeyboardEvent): boolean {
    const code = event.which ? event.which : event.keyCode;
    return !(code > 31 && (code < 48 || code > 57));
  }

  onSubmit(): void {
    if (this.form?.invalid || this.sending) return;
    this.send.emit();
  }

  /* ------------------------------------------------------------- reveal -- */

  ngAfterViewInit(): void {
    if (!isPlatformBrowser(this.platformId)) {
      this.viewState.set('shown');
      return;
    }

    const el = this.ccRoot?.nativeElement;
    if (!el) return;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced || el.getBoundingClientRect().top < window.innerHeight * 0.92) {
      this.viewState.set('shown');
      return;
    }

    this.io = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        this.zone.run(() => this.viewState.set('shown'));
        this.io?.disconnect();
      },
      { threshold: 0, rootMargin: '0px 0px -8% 0px' }
    );
    this.zone.runOutsideAngular(() => this.io?.observe(el));
  }

  ngOnDestroy(): void {
    this.io?.disconnect();
  }
}
